import { Throbber } from '@edgebox/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesUpDown } from '@fortawesome/pro-light-svg-icons/faAnglesUpDown';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import React from 'react';
import { Dropdown, DropdownProps, FormControl, InputGroup } from '@edgebox/react-components';
import { Link } from 'react-router-dom';

export interface ITopNavEntitySelectorLoadResult<TEntity> {
  items: TEntity[];
  total: number;
}

export interface ITopNavEntitySelectorProps<TEntity> {
  current?: TEntity;
  renderToggleLabel: (entity?: TEntity) => React.ReactNode;
  headerActions?: React.ReactNode;
  loadOptions: (query: string) => Promise<ITopNavEntitySelectorLoadResult<TEntity>>;
  renderOption: (entity: TEntity) => React.ReactNode;
  optionKey: (entity: TEntity) => string;
  onSelect: (entity: TEntity) => void;
  getOptionHref?: (entity: TEntity) => string;
  onReset?: () => void;
  toggleId: string;
  minWidth?: number;
  cacheKey?: string;
  emptyLabel?: string;
  entityNamePlural: string;
  /** The edge of the button the open menu lines up with, as the dropdown's `align` takes it; its start where not given. */
  menuAlign?: DropdownProps['align'];
}

interface ITopNavEntitySelectorState<TEntity> {
  menuOpen: boolean;
  query: string;
  options?: TEntity[];
  total?: number;
  loading: boolean;
  loadedKey?: string;
  loadedQuery?: string;
}

const SEARCH_DEBOUNCE_MS = 200;

export class TopNavEntitySelector<TEntity> extends React.Component<
  ITopNavEntitySelectorProps<TEntity>,
  ITopNavEntitySelectorState<TEntity>
> {
  state: ITopNavEntitySelectorState<TEntity> = {
    menuOpen: false,
    query: '',
    loading: false,
  };

  private searchInputElement: HTMLInputElement | null = null;
  private setSearchInput = (element: HTMLInputElement | null) => {
    this.searchInputElement = element;
  };
  private debounceTimer?: ReturnType<typeof setTimeout>;
  private requestSeq = 0;

  componentWillUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private async loadFor(query: string) {
    const cacheKey = this.props.cacheKey ?? '';
    if (this.state.loadedKey === cacheKey && this.state.loadedQuery === query && this.state.options) {
      return;
    }

    const seq = ++this.requestSeq;
    this.setState({ loading: true });
    try {
      const result = await this.props.loadOptions(query);
      if (seq !== this.requestSeq) {
        return;
      }
      this.setState({ options: result.items, total: result.total, loadedKey: cacheKey, loadedQuery: query });
    } finally {
      if (seq === this.requestSeq) {
        this.setState({ loading: false });
      }
    }
  }

  private handleToggle = (open: boolean) => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }
    this.setState({ menuOpen: open, query: open ? this.state.query : '' });
    if (open) {
      void this.loadFor(this.state.query);
      setTimeout(() => this.searchInputElement?.focus(), 0);
    }
  };

  private handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    this.setState({ query });
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      void this.loadFor(query);
    }, SEARCH_DEBOUNCE_MS);
  };

  // The keys typed into the search field stay with it, all but Escape: the menu closes on it and hands
  // the focus back to its button, as it does from anywhere else in the menu.
  private handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  private handleReset = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    this.props.onReset?.();
  };

  private closeMenu = () => {
    this.setState({ menuOpen: false, query: '' });
  };

  render() {
    const {
      current,
      renderToggleLabel,
      headerActions,
      renderOption,
      optionKey,
      onSelect,
      getOptionHref,
      onReset,
      toggleId,
      minWidth,
      emptyLabel,
      entityNamePlural,
      menuAlign,
    } = this.props;
    const { menuOpen, options, total, loading, query } = this.state;

    const minWidthValue = minWidth ?? 280;
    // The menu's width, read by the stylesheet (`.top-nav-entity-menu`): its minimum everywhere, and in
    // the top bar's one row its maximum as well.
    const menuWidthStyle: React.CSSProperties & Record<'--top-nav-entity-menu-width', string> = {
      '--top-nav-entity-menu-width': `${minWidthValue}px`,
    };
    const currentKey = current ? optionKey(current) : undefined;
    const placeholder = total === undefined ? `Search ${entityNamePlural}…` : `Search ${total} ${entityNamePlural}…`;

    // The toggle takes the width its container gives it and truncates the label, so the top bar can
    // shrink it on a narrow screen; only the open menu keeps a minimum width for the search field.
    return (
      <Dropdown
        show={menuOpen}
        onToggle={(open) => this.handleToggle(!!open)}
        autoClose="outside"
        align={menuAlign}
        className="w-100 min-width-0"
      >
        <Dropdown.Toggle
          variant=""
          caret={false}
          id={toggleId}
          className="top-nav-entity-toggle text-start ps-2 pe-2 pt-1 pb-1 border shadow-sm bg-white rounded d-inline-flex align-items-center w-100 justify-content-between"
        >
          <span className="d-inline-flex align-items-center me-2 min-width-0">{renderToggleLabel(current)}</span>
          <span className="d-inline-flex align-items-center flex-shrink-0">
            {onReset && (
              <span
                role="button"
                aria-label="Reset selection"
                onClick={this.handleReset}
                className="text-muted me-2 d-inline-flex align-items-center"
                style={{ cursor: 'pointer' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </span>
            )}
            <FontAwesomeIcon icon={faAnglesUpDown} className="text-muted" />
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="top-nav-entity-menu shadow-sm p-0" style={menuWidthStyle}>
          {headerActions ? (
            <>
              <div onClick={this.closeMenu}>{headerActions}</div>
              <Dropdown.Divider className="my-1" />
            </>
          ) : null}
          <div className="px-2 py-2" onClick={(event) => event.stopPropagation()}>
            <InputGroup className="top-nav-entity-search">
              <InputGroup.Text className="bg-white">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="text-muted" />
              </InputGroup.Text>
              <FormControl
                ref={this.setSearchInput}
                type="text"
                value={query}
                onChange={this.handleQueryChange}
                onKeyDown={this.handleSearchKeyDown}
                placeholder={placeholder}
                aria-label="Search"
              />
              <InputGroup.Text className="bg-white" style={{ minWidth: '32px', justifyContent: 'center' }}>
                {loading ? <Throbber /> : <span style={{ width: '1em' }} />}
              </InputGroup.Text>
            </InputGroup>
          </div>
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {loading && !options ? (
              <div className="px-3 py-2 text-muted d-flex align-items-center">
                <Throbber />
                <span className="ms-2">Loading…</span>
              </div>
            ) : options?.length ? (
              options.map((entity) => {
                const key = optionKey(entity);
                const isActive = key === currentKey;
                const className = `top-nav-entity-option w-100 px-3 py-2 rounded-0 d-block text-start border-0 bg-white text-decoration-none ${
                  isActive ? 'text-primary' : 'text-dark'
                }`;
                const href = getOptionHref?.(entity);
                if (href) {
                  return (
                    <Link
                      key={key}
                      to={href}
                      className={className}
                      onClick={() => {
                        this.closeMenu();
                        onSelect(entity);
                      }}
                    >
                      {renderOption(entity)}
                    </Link>
                  );
                }
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      this.closeMenu();
                      onSelect(entity);
                    }}
                    className={className}
                  >
                    {renderOption(entity)}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-muted">{emptyLabel ?? 'No options available'}</div>
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}
